import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { fmtCupo } from '../../utils/format';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart,
} from 'recharts';
import {
  Calendar, Users, Award, TrendingUp, ClipboardCheck, AlertCircle,
  GraduationCap, Truck, Search, X, User, CheckCircle2, XCircle, MinusCircle, Ban,
} from 'lucide-react';
import { formatDDMMYYYY } from '../../utils/date';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = {
  induccion: '#0F766E',
  conduccion: '#D97706',
  presente: '#10B981',
  ausente: '#EF4444',
  aprobado: '#059669',
  reprobado: '#DC2626',
  muted: '#94A3B8',
};

const CAP_INDUCCION_ID = '6f4f6ba0-ab5c-4dd5-b1df-61b2fbe6db67';
const CAP_CONDUCCION_ID = '54bc79e4-1489-4141-be55-7c4dd3b1281a';

export default function CapacitadorDashboard() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [stats, setStats] = useState(null);
  const [sesiones, setSesiones] = useState([]); // solo para "Próximas sesiones"
  const [loading, setLoading] = useState(true);
  const [mes, setMes] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const rangoActivo = !!(desde || hasta);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // 1 sola llamada agregada (reemplaza 230+ HTTP calls anteriores)
        const params = rangoActivo ? { desde: desde || undefined, hasta: hasta || undefined } : { mes };
        const [statsRes, sesRes] = await Promise.all([
          axios.get(`${API}/capacitador/dashboard-stats`, { headers, params }),
          axios.get(`${API}/sesiones`, { headers }).catch(() => ({ data: [] })),
        ]);
        setStats(statsRes.data);
        // Solo guardar próximas sesiones para el card lateral
        const upcoming = (sesRes.data || [])
          .filter(s => (s.capacitacion_id === CAP_INDUCCION_ID || s.capacitacion_id === CAP_CONDUCCION_ID)
            && new Date(s.fecha) >= new Date(new Date().toDateString()))
          .slice(0, 12);
        setSesiones(upcoming);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [headers, mes, desde, hasta, rangoActivo]);

  const kpis = stats?.kpis || { total: 0, presentes: 0, ausentes: 0, aprobados: 0, reprobados: 0, pendientes: 0, pct_asistencia: 0, pct_aprobacion: 0, sesiones_total: 0, sesiones_induccion: 0, sesiones_conduccion: 0 };
  const porCap = stats?.por_cap || [];
  const demografia = stats?.demografia;

  // Ocupación diaria (últimas 30 fechas para no saturar)
  const ocupacionPorDia = useMemo(() => {
    if (!stats?.ocupacion) return [];
    return stats.ocupacion.slice(-30).map(r => ({ ...r, dia: formatDia(r.fecha) }));
  }, [stats]);

  // Tendencia con % (últimas 30)
  const tendencia = useMemo(() => {
    if (!stats?.tendencia) return [];
    return stats.tendencia.slice(-30).map(r => ({
      dia: formatDia(r.fecha),
      Asistencia: r.total ? Math.round((r.presentes / r.total) * 100) : 0,
      Aprobación: r.presentes ? Math.round((r.aprobados / r.presentes) * 100) : 0,
    }));
  }, [stats]);

  const pieAsistencia = useMemo(() => [
    { name: 'Presentes', value: kpis.presentes, color: COLORS.presente },
    { name: 'Ausentes', value: kpis.ausentes, color: COLORS.ausente },
    { name: 'Pendientes', value: kpis.pendientes, color: COLORS.muted },
  ], [kpis.presentes, kpis.ausentes, kpis.pendientes]);

  const mesesDisponibles = stats?.meses_disponibles || [];
  const subtituloRango = useMemo(() => {
    if (rangoActivo) {
      const d = desde ? formatDDMMYYYY(desde) : '—';
      const h = hasta ? formatDDMMYYYY(hasta) : '—';
      return `Rango ${d} → ${h}`;
    }
    return formatMesLargo(mes) || 'Todos los periodos';
  }, [rangoActivo, desde, hasta, mes]);

  const limpiarRango = () => { setDesde(''); setHasta(''); };

  if (loading && !stats) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando dashboard...</div>;
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-slate-50 min-h-screen" data-testid="dashboard-capacitador">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-teal-700 mb-1">Panel del Capacitador</p>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Dashboard de Capacitación</h1>
          <p className="text-sm text-slate-500 mt-1">Seguimiento de Inducción y Conducción Lomas Bayas · {subtituloRango}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            disabled={loading || rangoActivo}
            className="px-3 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-slate-50 disabled:text-slate-400"
            data-testid="dashboard-mes-selector">
            <option value="todos">Todos los periodos</option>
            {mesesDisponibles.map((m) => (
              <option key={m} value={m}>{formatMesLargo(m)}</option>
            ))}
          </select>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${rangoActivo ? 'border-teal-300 bg-teal-50' : 'border-slate-200 bg-white'}`}>
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Desde</label>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              max={hasta || undefined}
              className="text-xs bg-transparent focus:outline-none text-slate-700"
              data-testid="dashboard-desde-input"
            />
            <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider ml-1">Hasta</label>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              min={desde || undefined}
              className="text-xs bg-transparent focus:outline-none text-slate-700"
              data-testid="dashboard-hasta-input"
            />
            {rangoActivo && (
              <button type="button" onClick={limpiarRango}
                className="ml-1 p-0.5 text-slate-500 hover:text-red-600 transition-colors"
                data-testid="dashboard-limpiar-rango"
                title="Limpiar rango">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <LegendChip color={COLORS.induccion} label="Inducción" icon={GraduationCap} />
          <LegendChip color={COLORS.conduccion} label="Conducción" icon={Truck} />
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Calendar} title="Sesiones programadas" value={kpis.sesiones_total}
          subtitle={`${kpis.sesiones_induccion} Inducción · ${kpis.sesiones_conduccion} Conducción`} color="#0F766E" />
        <Kpi icon={Users} title="Inscritos" value={kpis.total}
          subtitle={`${kpis.pendientes} pendientes de evaluar`} color="#1D4ED8" />
        <Kpi icon={ClipboardCheck} title="Asistencia" value={`${Math.round(kpis.pct_asistencia)}%`}
          subtitle={`${kpis.presentes} presentes · ${kpis.ausentes} ausentes`} color="#10B981" />
        <Kpi icon={Award} title="Aprobación" value={`${Math.round(kpis.pct_aprobacion)}%`}
          subtitle={`${kpis.aprobados} aprobados · ${kpis.reprobados} reprobados`} color="#D97706" />
      </div>

      {/* Búsqueda por RUT — Historial de capacitaciones */}
      <HistorialTrabajador headers={headers} />

      {/* Charts grid */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card title="Asistencia vs Ausencia" subtitle="Total evaluados">
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={pieAsistencia} dataKey="value" nameKey="name" cx="50%" cy="50%"
                innerRadius={55} outerRadius={85} paddingAngle={2}>
                {pieAsistencia.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Resultados por Capacitación" subtitle="Aprobados · Reprobados · Ausentes" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={porCap} barCategoryGap={30}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Aprobados" stackId="a" fill={COLORS.aprobado} radius={[0, 0, 0, 0]} />
              <Bar dataKey="Reprobados" stackId="a" fill={COLORS.reprobado} />
              <Bar dataKey="Ausentes" stackId="a" fill={COLORS.muted} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Ocupación diaria" subtitle="Inscritos por día" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={ocupacionPorDia}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="dia" stroke="#64748b" fontSize={10} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Inducción" fill={COLORS.induccion} stackId="b" />
              <Bar dataKey="Conducción" fill={COLORS.conduccion} stackId="b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Tendencia diaria" subtitle="% Asistencia y Aprobación">
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={tendencia}>
              <defs>
                <linearGradient id="gAsist" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gApr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D97706" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#D97706" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="dia" stroke="#64748b" fontSize={10} />
              <YAxis stroke="#64748b" fontSize={11} domain={[0, 100]} />
              <Tooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="Asistencia" stroke="#10B981" fill="url(#gAsist)" strokeWidth={2} />
              <Area type="monotone" dataKey="Aprobación" stroke="#D97706" fill="url(#gApr)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Distribución por Género" subtitle={`${demografia?.total_trabajadores || 0} trabajadores`}>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={demografia?.genero || []} dataKey="value" nameKey="name" cx="50%" cy="50%"
                innerRadius={45} outerRadius={80} paddingAngle={2}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                labelLine={false}>
                {(demografia?.genero || []).map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Distribución por Edad" subtitle={`Edad promedio: ${demografia?.edad_promedio || 0} años`} className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={demografia?.edad_rangos || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="rango" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip />
              <Bar dataKey="count" name="Trabajadores" fill="#7C3AED" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Próximas sesiones" subtitle="Sesiones programadas con cupos">
          <div className="space-y-2 max-h-[230px] overflow-y-auto pr-1">
            {sesiones
              .filter(s => new Date(s.fecha) >= new Date(new Date().toDateString()))
              .slice(0, 6)
              .map(s => {
                const cap = s.capacitacion_id === CAP_INDUCCION_ID ? 'Inducción' : 'Conducción';
                const color = cap === 'Inducción' ? COLORS.induccion : COLORS.conduccion;
                const ocupacion = Math.round(((s.inscritos || 0) / (s.cupo_maximo || 20)) * 100);
                return (
                  <div key={s.id} className="flex items-center gap-3 p-2 rounded-lg border border-slate-100 bg-slate-50/50">
                    <div className="w-2 h-10 rounded-full" style={{ backgroundColor: color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-slate-800 truncate">{cap}</p>
                      <p className="text-[10px] text-slate-500">{formatDia(s.fecha)} · {s.hora_inicio?.slice(0, 5)}-{s.hora_fin?.slice(0, 5)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-slate-500">{s.inscritos || 0}/{fmtCupo(s.cupo_maximo)}</p>
                      <div className="w-16 h-1.5 rounded-full bg-slate-200 mt-0.5 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${ocupacion}%`, backgroundColor: color }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            {sesiones.length === 0 && <p className="text-xs text-slate-400 text-center py-8">Sin sesiones próximas</p>}
          </div>
        </Card>

        <Card title="Alertas operativas" subtitle="Requieren acción">
          <div className="space-y-2">
            <AlertBox type="warning" icon={AlertCircle}
              title={`${kpis.pendientes} inscripciones sin evaluar`}
              desc="Asigna Presente/Ausente y nota para completar el ciclo." />
            <AlertBox type="danger" icon={TrendingUp}
              title={`${kpis.reprobados} trabajadores reprobados`}
              desc="Reagendar para segunda evaluación." />
            <AlertBox type="info" icon={GraduationCap}
              title={`Nota promedio: ${(kpis.nota_promedio || 0).toFixed(1)}`}
              desc="Por encima del mínimo de aprobación (85)." />
          </div>
        </Card>
      </div>
    </div>
  );
}

function formatDia(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  const dias = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];
  return `${dias[d.getDay()]} ${d.getDate().toString().padStart(2, '0')}`;
}

function formatMesLargo(mes) {
  if (!mes || mes === 'todos') return '';
  const [y, m] = mes.split('-');
  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const idx = parseInt(m, 10) - 1;
  if (idx < 0 || idx > 11) return '';
  return `${meses[idx]} ${y}`;
}

function Kpi({ icon: Icon, title, value, subtitle, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15`, border: `1px solid ${color}40` }}>
          <Icon className="w-4.5 h-4.5" style={{ color }} strokeWidth={2.2} />
        </div>
      </div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{title}</p>
      <p className="text-2xl font-black mt-1 text-slate-900">{value}</p>
      <p className="text-[11px] text-slate-500 mt-1">{subtitle}</p>
    </div>
  );
}

function Card({ title, subtitle, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-200 p-5 ${className}`}>
      <div className="mb-3">
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function LegendChip({ color, label, icon: Icon }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold rounded-full"
      style={{ backgroundColor: `${color}15`, color, border: `1px solid ${color}40` }}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function AlertBox({ type, icon: Icon, title, desc }) {
  const colors = {
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-600' },
    danger: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-600' },
    info: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', icon: 'text-slate-600' },
  }[type];
  return (
    <div className={`flex gap-2 p-3 rounded-lg border ${colors.bg} ${colors.border}`}>
      <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${colors.icon}`} />
      <div>
        <p className={`text-xs font-bold ${colors.text}`}>{title}</p>
        <p className={`text-[11px] ${colors.text} opacity-80`}>{desc}</p>
      </div>
    </div>
  );
}

// ── Widget: búsqueda por RUT — historial actual e histórico ──
function HistorialTrabajador({ headers }) {
  const [rutInput, setRutInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null); // { trabajador, inscripciones, resumen }

  const buscar = async (e) => {
    e?.preventDefault();
    setError('');
    setData(null);
    if (!rutInput.trim() || rutInput.trim().length < 4) {
      setError('Ingresa un RUT válido (ej. 12.345.678-9)');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/capacitador/trabajador-historial`, {
        headers, params: { rut: rutInput.trim() },
      });
      setData(r.data);
      if (!r.data?.encontrado) setError('No se encontraron capacitaciones para este RUT.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al buscar el trabajador.');
    }
    setLoading(false);
  };

  const limpiar = () => {
    setRutInput(''); setData(null); setError('');
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="dashboard-historial-rut">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Search className="w-4 h-4 text-teal-600" /> Historial de capacitaciones por RUT
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Consulta las capacitaciones actuales e históricas de un trabajador.
          </p>
        </div>
        <form onSubmit={buscar} className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={rutInput}
              onChange={(e) => setRutInput(e.target.value)}
              placeholder="Ej. 12.345.678-9"
              className="pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              data-testid="dashboard-rut-input"
            />
            {rutInput && (
              <button type="button" onClick={limpiar}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                data-testid="dashboard-rut-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button type="submit" disabled={loading}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="dashboard-rut-search-btn">
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </form>
      </div>

      {error && !loading && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="dashboard-rut-error">
          {error}
        </div>
      )}

      {data?.encontrado && (
        <div className="space-y-4" data-testid="dashboard-rut-results">
          {/* Encabezado del trabajador */}
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-teal-50 to-transparent rounded-lg border border-teal-100">
            <div className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center shrink-0">
              <User className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900 truncate">
                {`${data.trabajador.nombre} ${data.trabajador.apellido}`.trim() || 'Trabajador'}
              </p>
              <p className="text-[11px] text-slate-500">
                RUT {data.trabajador.rut} · {data.trabajador.empresa_nombre || 'Sin empresa asignada'}
                {data.trabajador.cargo ? ` · ${data.trabajador.cargo}` : ''}
              </p>
            </div>
            <span className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">
              {data.inscripciones.length} inscripción(es)
            </span>
          </div>

          {/* Resumen por capacitación */}
          {data.resumen.length > 0 && (
            <div className="grid md:grid-cols-2 gap-3">
              {data.resumen.map((s, i) => (
                <div key={i} className="p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-slate-800">{s.capacitacion}</p>
                    {s.bloqueado ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold inline-flex items-center gap-1">
                        <Ban className="w-3 h-3" /> Bloqueado
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">Activo</span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <StatMini label="Total" value={s.total} color="text-slate-700" />
                    <StatMini label="Aprob." value={s.aprobados} color="text-emerald-700" />
                    <StatMini label="Reprob." value={s.reprobados} color="text-red-700" />
                    <StatMini label="Ausentes" value={s.ausentes} color="text-slate-500" />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
                    <span>Última: {formatDDMMYYYY(s.ultima_fecha) || '—'}</span>
                    {s.max_intentos !== undefined && (
                      <span className="font-semibold">
                        Intentos: {s.intentos_consumidos || 0}/{s.max_intentos}
                        {s.post_habilitacion ? ' (post-habilitación)' : ''}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tabla de inscripciones */}
          {data.inscripciones.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-600 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                      <th className="text-left px-3 py-2 font-semibold">Capacitación</th>
                      <th className="text-left px-3 py-2 font-semibold">Horario</th>
                      <th className="text-center px-3 py-2 font-semibold">Asistencia</th>
                      <th className="text-center px-3 py-2 font-semibold">Resultado</th>
                      <th className="text-center px-3 py-2 font-semibold">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.inscripciones.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">{formatDDMMYYYY(r.fecha)}</td>
                        <td className="px-3 py-2 text-slate-700">{r.capacitacion_nombre}</td>
                        <td className="px-3 py-2 text-slate-500">
                          {r.hora_inicio?.slice(0, 5)}-{r.hora_fin?.slice(0, 5)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <AsistenciaBadge value={r.asistencia} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <ResultadoBadge value={r.resultado} />
                        </td>
                        <td className="px-3 py-2 text-center text-slate-700 font-semibold">
                          {r.nota != null ? r.nota : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatMini({ label, value, color }) {
  return (
    <div>
      <p className={`text-lg font-black ${color}`}>{value}</p>
      <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}

function AsistenciaBadge({ value }) {
  if (value === 'presente') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
      <CheckCircle2 className="w-3 h-3" /> Presente
    </span>;
  }
  if (value === 'ausente') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
      <XCircle className="w-3 h-3" /> Ausente
    </span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">
    <MinusCircle className="w-3 h-3" /> Pendiente
  </span>;
}

function ResultadoBadge({ value }) {
  if (value === 'aprobado') {
    return <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">APROBADO</span>;
  }
  if (value === 'reprobado') {
    return <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">REPROBADO</span>;
  }
  return <span className="text-slate-400 text-[10px]">—</span>;
}
